import type { PropsWithChildren } from 'react';
import React, { useEffect, useMemo } from 'react';
import type { TranslationPath as CommonTranslationPath } from 'c-translation';
import { useCommonTranslation } from 'c-translation';
import type {
    BoxProps,
    DialogContentProps as MuiDialogContentProps,
    DialogProps as MuiDialogProps,
    DialogTitleProps,
    TypographyProps,
} from '@mui/material';
import {
    Box,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    IconButton,
    Stack,
    Typography,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import merge from 'deepmerge';
import DiscardChangesDialog from './DiscardChangesDialog';
import { useDiscardChangesGuard } from './useDiscardChangesGuard';

type Props = PropsWithChildren<{
    title?: CommonTranslationPath | React.ReactNode;
    titleVariant?: TypographyProps['variant'];
    description?: CommonTranslationPath | React.ReactNode;
    titleDivider?: boolean;

    titleSx?: DialogTitleProps['sx'];

    onClose: (e?: React.MouseEvent | React.KeyboardEvent) => void;
    open: boolean;
    /**
     * The dialog holds input that closing would lose. While true, a backdrop click is ignored and
     * Escape or the close button asks "Discard changes?" first.
     */
    isDirty?: boolean;
    maxWidth?: MuiDialogProps['maxWidth'];
    scroll?: MuiDialogProps['scroll'];

    MuiDialogProps?: Partial<MuiDialogProps>;
    MuiDialogContentProps?: Partial<MuiDialogContentProps>;

    contentHeight?: BoxProps['height'];

    actions?: React.ReactNode;
}>;

const PaperProps = { sx: { borderRadius: 2.5 } };
const dialogTitleSx = { position: 'relative', wordBreak: 'break-all' };
const actionsSx = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    pt: 2,
    pb: 5,
    px: 5,
};
const descriptionSx = { textAlign: 'center', whiteSpace: 'pre-wrap' };

const DialogV2: React.FC<Props> = ({
    MuiDialogProps,
    MuiDialogContentProps,
    titleSx,
    contentHeight,
    onClose,
    open,
    isDirty = false,
    maxWidth = 'sm',
    scroll = 'paper',
    title,
    titleVariant = 'h3',
    description,
    titleDivider = false,
    actions,
    children,
}) => {
    const t = useCommonTranslation();
    const titleContent = typeof title === 'string' ? t(title) : title;
    const descriptionContent = typeof description === 'string' ? t(description) : description;
    const mergedContentSx = useMemo(
        () =>
            merge(
                { display: 'flex', height: contentHeight },
                (MuiDialogContentProps?.sx ?? {}) as any,
            ),
        [MuiDialogContentProps, contentHeight],
    );
    const mergedTitleSx = useMemo<DialogTitleProps['sx']>(
        () => merge(dialogTitleSx, (titleSx ?? {}) as any),
        [titleSx],
    );
    const { requestClose, onDialogClose, confirmProps } = useDiscardChangesGuard(isDirty, onClose);
    // Closed some other way (saved, say) while asking: don't ask again on the next open.
    const { onKeepEditing } = confirmProps;
    useEffect(() => {
        if (!open) onKeepEditing();
    }, [open, onKeepEditing]);

    return (
        <Dialog
            open={open}
            className="open-modal"
            maxWidth={maxWidth}
            fullWidth
            scroll={scroll}
            PaperProps={PaperProps}
            {...MuiDialogProps}
            // After the spread, so the dirty guard can't be bypassed through MuiDialogProps.
            onClose={onDialogClose}
        >
            <DiscardChangesDialog {...confirmProps} />
            <DialogTitle sx={mergedTitleSx}>
                <Box position="absolute" right={0} top={0}>
                    <IconButton onClick={requestClose} aria-label={t('Dialog.defaultCloseLabel')}>
                        <Close fontSize="inherit" />
                    </IconButton>
                </Box>
                {titleContent && (
                    <Box mt={1}>
                        <Typography variant={titleVariant} textAlign="center">
                            {titleContent}
                        </Typography>
                    </Box>
                )}
            </DialogTitle>
            {titleDivider && <Divider />}

            <DialogContent {...MuiDialogContentProps} sx={mergedContentSx}>
                <Stack spacing={2} flex={1} maxWidth="100%">
                    {descriptionContent && (
                        <DialogContentText sx={descriptionSx}>
                            {descriptionContent}
                        </DialogContentText>
                    )}
                    {children}
                </Stack>
            </DialogContent>
            {actions && <DialogActions sx={actionsSx}>{actions}</DialogActions>}
        </Dialog>
    );
};

export default DialogV2;
